/**
 * AssetLoader - Asset loading and management
 *
 * Handles preloading of images and audio files with progress tracking.
 */

export class AssetLoader {
  constructor() {
    this.assets = new Map();
    this.loadQueue = [];
    this.loaded = 0;
    this.total = 0;
    this.failed = [];
    this.onProgress = null;
    this.onComplete = null;
    this.onError = null;
  }

  /**
   * Queue an image for loading
   */
  queueImage(key, path) {
    this.loadQueue.push({
      type: 'image',
      key,
      path
    });
    this.total++;
    return this;
  }

  /**
   * Queue multiple images
   */
  queueImages(imageMap) {
    for (const [key, path] of Object.entries(imageMap)) {
      this.queueImage(key, path);
    }
    return this;
  }

  /**
   * Queue an audio file for loading
   */
  queueAudio(key, path) {
    this.loadQueue.push({
      type: 'audio',
      key,
      path
    });
    this.total++;
    return this;
  }

  /**
   * Queue multiple audio files
   */
  queueAudios(audioMap) {
    for (const [key, path] of Object.entries(audioMap)) {
      this.queueAudio(key, path);
    }
    return this;
  }

  /**
   * Load an image
   */
  loadImage(key, path) {
    return new Promise((resolve) => {
      const img = new Image();

      img.onload = () => {
        this.assets.set(key, img);
        this.loaded++;
        this.updateProgress();
        resolve(img);
      };

      img.onerror = () => {
        const error = new Error(`Failed to load image: ${path}`);
        this.failed.push({ key, path, error });
        this.loaded++;
        this.updateProgress();
        if (this.onError) {
          this.onError(error, key, path);
        }
        // Don't reject - allow loading to continue
        resolve(null);
      };

      img.src = path;
    });
  }

  /**
   * Load an audio file
   */
  loadAudio(key, path) {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.preload = 'auto';

      const onCanPlay = () => {
        this.assets.set(key, audio);
        this.loaded++;
        this.updateProgress();
        cleanup();
        resolve(audio);
      };

      const onError = () => {
        const error = new Error(`Failed to load audio: ${path}`);
        this.failed.push({ key, path, error });
        this.loaded++;
        this.updateProgress();
        cleanup();
        if (this.onError) {
          this.onError(error, key, path);
        }
        // Don't reject - allow loading to continue
        resolve(null);
      };

      const cleanup = () => {
        audio.removeEventListener('canplaythrough', onCanPlay);
        audio.removeEventListener('error', onError);
      };

      audio.addEventListener('canplaythrough', onCanPlay, { once: true });
      audio.addEventListener('error', onError, { once: true });
      audio.src = path;
      audio.load();
    });
  }

  /**
   * Update loading progress
   */
  updateProgress() {
    if (this.onProgress) {
      const progress = this.total > 0 ? this.loaded / this.total : 0;
      this.onProgress(progress, this.loaded, this.total);
    }

    if (this.loaded >= this.total && this.onComplete) {
      this.onComplete(this.failed.length > 0 ? this.failed : null);
    }
  }

  /**
   * Start loading all queued assets
   */
  async load() {
    if (this.total === 0) {
      if (this.onComplete) {
        this.onComplete(null);
      }
      return this.assets;
    }

    const promises = this.loadQueue.map(item => {
      if (item.type === 'image') {
        return this.loadImage(item.key, item.path);
      } else if (item.type === 'audio') {
        return this.loadAudio(item.key, item.path);
      }
    });

    await Promise.all(promises);
    return this.assets;
  }

  /**
   * Get a loaded asset by key
   */
  get(key) {
    return this.assets.get(key);
  }

  /**
   * Check if an asset exists
   */
  has(key) {
    return this.assets.has(key);
  }

  /**
   * Get all loaded assets
   */
  getAll() {
    return this.assets;
  }

  /**
   * Get loading progress (0 to 1)
   */
  getProgress() {
    return this.total > 0 ? this.loaded / this.total : 0;
  }

  /**
   * Check if loading is complete
   */
  isComplete() {
    return this.loaded >= this.total;
  }

  /**
   * Get failed assets
   */
  getFailed() {
    return this.failed;
  }

  /**
   * Clear all assets and reset
   */
  clear() {
    this.assets.clear();
    this.loadQueue = [];
    this.loaded = 0;
    this.total = 0;
    this.failed = [];
  }
}
